import { FastField, useFormikContext } from 'formik';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { Button, Grid } from '@mui/material';
import { TrimsModal } from '@modules/ContactManager/components/Tabs/components/Assets/components/TrimsModal';
import { IVehicleTrim } from '@modules/ContactManager/components/Tabs/components/Assets/interfaces';
import { requestPullVehicleData } from '@modules/ContactManager/components/Tabs/components/Assets/requests';
import { PhoenixGridHeader } from '@modules/admin/Tools/LenderUtility/shared/PhoenixGridHeader';
import { FormPart } from '@modules/shared/components/FormPart';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { IOptionResponse } from '@shared/interfaces';
import { ASSET_TYPE_OPTIONS, assetConditions } from '@shared/constants/lists';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { TextInputField } from '@shared/fields/TextInputField';
import { PageWrapper, ShadowDivider } from '@shared/styledComponents';
import { getDateByYearsMonths, getDiffYearsMonths, getOptions } from '@shared/commonFunctions';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { SaveButton } from '@shared/inputs/Buttons';
import { ScrollPageContent } from '@styles/CommonStyles';

import { ICreateContact, ICreateContactFormProps, ILeadSource } from '../../queries';
import { 
  driversLicenseConfig, 
  currentAddressConfig, 
  previousAddressConfig, 
  employerInfoConfig, 
  individualInfoConfigRight, 
  individualInfoConfigLeftPart 
} from '../../utils/formConfigs';


export const CreateContactForm = ({ leadSources, leadSourcesError, isLoading }: ICreateContactFormProps) => {
  const { values, setFieldValue } = useFormikContext<ICreateContact>();

  const [loading, setLoading] = useState(false);
  const [makeId, setMakeId] = useState(0);

  const { data: years = [], error: yearsError } = useSWR<IOptionResponse[]>(
    values?.asset?.assetType ? `${SERVICES.ASSET}/v2/kbb/years/${values?.asset?.assetType}` : null);
  const { data: makes = [], error: makesError } = useSWR<IOptionResponse[]>(
    values?.asset?.year && values?.asset?.assetType ? `${SERVICES.ASSET}/v2/kbb/makes/${values?.asset?.year}/${values?.asset?.assetType}` : null,
  );
  const { data: models = [], error: modelsError } = useSWR<IOptionResponse[]>(
    values?.asset?.year && makeId && values?.asset?.assetType ? `${SERVICES.ASSET}/v2/kbb/models/${makeId}/${values?.asset?.year}/${values?.asset?.assetType}` : null,
  );

  const [isTrimsModalOpen, setTrimsModalOpen] = useState(0);
  const [kbbTrims, setKbbTrims] = useState<IVehicleTrim[]>([]);
  const [nadaTrims, setNadaTrims] = useState<IVehicleTrim[]>([]);

  useEffect(() => {
    const makeObj: IOptionResponse = makes?.find((make) => make.name === values.asset.make) || ({} as IOptionResponse);

    setMakeId(makeObj.id);
  }, [values.asset.make, makes]);

  const pullData = useCallback(async() => {
    const { vin, mileage, assetType } = values.asset;

    if (assetType === '' || !mileage) {
      Notistack.generalError('Odometer and Asset Type is Required to Pull Trim Details');
    } else if (vin) {
      setLoading(true);

      const data = await requestPullVehicleData(vin);

      setLoading(false);
      
      if (data) {
        const { make, model, year, kbbTrim, nadaTrim } = data;
        setFieldValue?.('asset.year', year);
        setFieldValue?.('asset.model', model);
        setFieldValue?.('asset.make', make);
        if (
          (data.availableKbbTrims?.length === 1 && data.availableNadaTrims?.length === 0) ||
          (data.availableKbbTrims?.length === 0 && data.availableNadaTrims?.length === 1) ||
          (data.availableKbbTrims?.length === 1 && data.availableNadaTrims?.length === 1)
        ) {
          setFieldValue('asset.kbbTrim', kbbTrim);
          setFieldValue('asset.nadaTrim', nadaTrim);
        } else if (data.availableKbbTrims && data.availableNadaTrims) {
          setKbbTrims(data.availableKbbTrims);
          setNadaTrims(data.availableNadaTrims);
          setTrimsModalOpen(prevState => prevState + 1);
        } else {
          Notistack.generalError('Trim data is not available!');
        }
      }
    } else {
      Notistack.generalError('Vin is required!');
    }
  }, [values.asset.vin, values.asset.mileage, values.asset.assetType, requestPullVehicleData]);

  const secondaryLeadSources: ILeadSource[] = useMemo(
    () => leadSources?.filter((source: ILeadSource) => source.isSecondary),
    [leadSources],
  );

  const individualInfoConfigLeft = useMemo(
    () => [
      {
        width: '100%',
        name: 'secondarySource',
        options: getOptions(secondaryLeadSources),
        isLoading: !leadSources && !leadSourcesError,
        shouldUpdate: () => true,
        isFitContentWidth: true,
        component: SelectAutocompleteField,
      },
      ...individualInfoConfigLeftPart,
    ],
    [leadSources, leadSourcesError]
  );

  const assetInfoConfig = useMemo(() => ([
    {
      width: '100%',
      name: 'asset.assetType',
      options: ASSET_TYPE_OPTIONS,
      component: SelectAutocompleteField,
    },
    {
      width: '100%',
      name: 'asset.mileage',
      label: 'Odometer',
      component: NumberInputField,
    },
    {
      width: '100%',
      name: 'asset.year',
      label: 'Year',
      options: getOptions(years, 'name', 'name'),
      isLoading: !years && !yearsError,
      shouldUpdate: () => true,
      component: SelectAutocompleteField,
    },
    {
      width: '50%',
      name: 'asset.make',
      label: 'Make',
      options: getOptions(makes, 'name', 'name'),
      isLoading: !makes && !makesError && values?.asset?.year,
      shouldUpdate: () => true,
      component: SelectAutocompleteField,
    },
    {
      width: '50%',
      name: 'asset.model',
      label: 'Model',
      options: getOptions(models, 'name', 'name'),
      isLoading: !models && !modelsError && values?.asset?.make,
      shouldUpdate: () => true,
      component: SelectAutocompleteField,
    },
    {
      width: '100%',
      name: 'asset.kbbTrim',
      label: 'KBB Trim',
      component: TextInputField,
    },
    {
      width: '100%',
      name: 'asset.nadaTrim',
      label: 'JD Power Trim',
      component: TextInputField,
    },
    {
      width: '100%',
      name: 'asset.assetCondition',
      label: 'Condition',
      options: assetConditions,
      component: SelectAutocompleteField,
    },
  ]), [pullData, models, modelsError, values?.asset, makes, makesError, years, yearsError]);

  const onAfterChangeMonthsYears = useCallback(
    (prefix: keyof Pick<ICreateContact, 'currentEmployer' | 'previousEmployer'>) => {
      const months = values[prefix].months;
      const years = values[prefix].years;

      if (typeof months === 'number' && typeof years === 'number') {
        setFieldValue(`${prefix}.startDate`, getDateByYearsMonths(years, months, true));
      }
    },
    [values]
  );  

  const onAfterChangeStartDate = useCallback(
    (prefix: keyof Pick<ICreateContact, 'currentEmployer' | 'previousEmployer'>) => {
      const startDate = values[prefix].startDate;

      if (startDate) {
        setFieldValue(`${prefix}.years`, getDiffYearsMonths(startDate)[0]);
        setFieldValue(`${prefix}.months`, getDiffYearsMonths(startDate)[1]);
      }
    },
    [values]
  );  

  const formConfig = useMemo(
    () => ({
      individualInfoConfigLeft,
      individualInfoConfigRight,
      driversLicenseConfig,
      assetInfoConfig,
      currentAddress: currentAddressConfig('currentAddress'),
      previousAddress: previousAddressConfig('previousAddress'),
      currentEmployerInfo: employerInfoConfig(true, 'currentEmployer', () => onAfterChangeMonthsYears('currentEmployer'), () => onAfterChangeStartDate('currentEmployer')),
      previousEmployerInfo: employerInfoConfig(false, 'previousEmployer'),
    }),
    [individualInfoConfigLeft, individualInfoConfigRight, driversLicenseConfig, assetInfoConfig]
  );

  return (
    <>
      <PageWrapper>
        <PhoenixGridHeader title="Main Info">
          <SaveButton width="100px"  isLoading={isLoading} />
        </PhoenixGridHeader>
        <ShadowDivider />

        <ScrollPageContent>
          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              md={6}
            >
              <FormPart
                useFastFields
                name="Individual Info"
                config={formConfig.individualInfoConfigLeft}
              />
            </Grid>

            <Grid
              item
              md={6}
            >
              <FormPart
                useFastFields
                name="&nbsp;"
                config={formConfig.individualInfoConfigRight}
              />

              <FormPart
                sx={{ mb: '55px' }}
                name="Driver's License Information"
                config={formConfig.driversLicenseConfig}
              />
            </Grid>

            <Grid
              item
              md={6}
            >
              <FormPart
                useFastFields
                name="Current Address"
                config={formConfig.currentAddress}
              />
            </Grid>

            <Grid
              item
              md={6}
            >
              <FormPart
                useFastFields
                name="Previous Address"
                config={formConfig.previousAddress}
              />
            </Grid>

            <Grid
              item
              md={6}
            >
              <FormPart
                useFastFields
                name="Current Employer Info"
                config={formConfig.currentEmployerInfo}
              />
            </Grid>

            <Grid
              item
              md={6}
            >
              <FormPart
                useFastFields
                name="Previous Employer Info"
                config={formConfig.previousEmployerInfo}
              />
            </Grid>

            <Grid
              item
              md={6}
            >
              <FormPart
                useFastFields
                name="Asset"
                config={formConfig.assetInfoConfig.slice(0, 2)}
              />

              <Grid container>
                <Grid
                  item
                  width="70%"
                >
                  <FastField
                    useFastFields
                    name="asset.vin"
                    label="VIN"
                    value={values?.asset.vin?.toUpperCase()}
                    component={TextInputField}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setFieldValue?.('asset.vin', event?.target?.value?.toUpperCase())
                    }
                  />
                </Grid>

                <Grid
                  item
                  width="10%"
                >
                  <Button
                    type="button"
                    variant="text"
                    color="info"
                    size="small"
                    onClick={pullData}
                  >
                    Pull
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid
              item
              md={6}
            >
              <FormPart
                useFastFields
                name="&nbsp;"
                config={formConfig.assetInfoConfig.slice(2, formConfig.assetInfoConfig.length)}
              />
            </Grid>
          </Grid>
        </ScrollPageContent>
      </PageWrapper>

      <LoaderBackdrop
        open={loading}
        handleClose={() => setLoading(false)}
      />

      {!!isTrimsModalOpen && (
        <TrimsModal
          trimModalPosition={isTrimsModalOpen}
          handleSave={() => setTrimsModalOpen(0)}
          handleClose={() => setTrimsModalOpen(0)}
          kbbTrims={kbbTrims}
          nadaTrims={nadaTrims}
          kbbFieldName="asset.kbbTrim"
          nadaFieldName="asset.nadaTrim"
        />
      )}
    </>
  );
};
